/* istanbul ignore file */
import React, { useEffect, useState } from 'react';
import {
  Alignment,
  BlockQuote,
  Bold,
  ClassicEditor,
  Essentials,
  EventInfo,
  Heading,
  Italic,
  Link,
  List,
  Mention,
  Paragraph,
  RemoveFormat,
  Strikethrough,
  Table,
  TableToolbar,
  Underline,
  Undo,
} from 'ckeditor5';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import 'ckeditor5/ckeditor5.css';
import BaseInputLayout, {
  COMMON_INPUT_LAYOUT_DEFAULT_PROPS,
  getBaseInputLayoutProps,
  getCommonInputProps,
} from './BaseInputLayout';
import {
  CKEditorEventType,
  WysiwygInputLayoutProps,
  WysiwygInputLayoutType,
  CommonInputLayoutProps,
} from 'framework/form';
import { turnUrlIntoLink } from '../../../../utils';
import ResizableHeight from './CKEditorPluggins/ResizableHeightPlugin';
import Box from '@mui/material/Box';
import GlobalStyles from '@mui/material/GlobalStyles';

const defaultOnPaste = async ({ content }: CKEditorEventType) => Promise.resolve(content);

const randomInteger = (min: number, max: number) => {
  const rand = min - 0.5 + Math.random() * (max - min + 1);
  return Math.round(rand);
};

const DEFAULT_TOOLBAR = [
  'bold',
  'italic',
  'underline',
  'strikethrough',
  '|',
  'removeFormat',
  '|',
  'bulletedList',
  'numberedList',
  '|',
  'blockQuote',
  '|',
  'link',
  '|',
  'insertTable',
  '|',
  'heading',
];

const WysiwygInputLayout: WysiwygInputLayoutType = ({
  onChangeCallback: originalOnChangeCallback,
  onPasteCallback = defaultOnPaste,
  setRefreshDataCallback,
  editorConfig = DEFAULT_TOOLBAR,
  ...otherProps
}: WysiwygInputLayoutProps) => {
  const props = {
    ...COMMON_INPUT_LAYOUT_DEFAULT_PROPS,
    ...otherProps,
  };

  const { value, onBlur, onFocus, className, disabled } = getCommonInputProps(props, false);
  const hasError = Boolean(props.errorMessages?.length);

  const [data, setData] = useState(value);

  const onChangeCallback = (_event: EventInfo, editor: any) => {
    const editorValue = editor.getData().replace(/(\r\n|\n|\r)/gm, '');

    if (originalOnChangeCallback) {
      originalOnChangeCallback({
        target: {
          value: turnUrlIntoLink(editorValue),
        },
      });
    }
  };

  const handlePaste = (_event: EventInfo, args: any) => {
    const { files } = args.dataTransfer;
    const textContent = args.content;
    onPasteCallback({ files, content: textContent });
  };

  useEffect(() => {
    // CKEditor verifies that previous state != new state. We should add few spaces to force
    // updating
    const refreshData = (newData: string) => setData(newData + ' '.repeat(randomInteger(1, 30)));

    if (setRefreshDataCallback) {
      setRefreshDataCallback(refreshData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <BaseInputLayout {...getBaseInputLayoutProps(props as unknown as CommonInputLayoutProps)}>
      <GlobalStyles styles={theme => ({ '.ck-balloon-panel': { zIndex: theme.zIndex.modal + 100 + ' !important' } })} />
      <Box
        className={className}
        sx={{
          width: '100%',
          border: '1px solid',
          borderColor: hasError ? 'error.main' : 'common.commonInputBorder',
          borderRadius: '0.25rem',
          '& .ck.resizable-mode .ck.ck-editor__main': {
            resize: 'vertical',
            overflow: 'auto',
            height: '54.8px',
            minHeight: '54.8px',
            maxHeight: '100vh',
          },
          '& .ck.resizable-mode .ck.ck-content.ck-editor__editable, & .ck.height-mode .ck.ck-content.ck-editor__editable':
            {
              height: 'auto !important',
              minHeight: '100%',
            },
          '& .ck .ck.ck-editor__main': {
            border: '1px solid var(--ck-color-base-border)',
            borderRadius: 'var(--ck-border-radius)',
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
          },
          '& .ck .ck.ck-editor__main.ck-focused': { borderColor: 'var(--ck-color-focus-border)' },
          '& .ck .ck.ck-content.ck-editor__editable': { border: '0 !important' },
          '& .ck.ck-toolbar.ck-toolbar_grouping > .ck-toolbar__items': { flexWrap: 'wrap' },
        }}
      >
        <CKEditor
          disabled={disabled}
          data={data}
          editor={ClassicEditor}
          config={{
            initialData: data,
            toolbar: {
              items: editorConfig,
            },
            extraPlugins: [ResizableHeight],
            plugins: [
              Table,
              TableToolbar,
              Heading,
              Bold,
              Essentials,
              Italic,
              Mention,
              Paragraph,
              Undo,
              Underline,
              Strikethrough,
              RemoveFormat,
              List,
              BlockQuote,
              Alignment,
              Link,
            ],
            licenseKey: 'GPL',
          }}
          onChange={onChangeCallback}
          onBlur={onBlur}
          onFocus={onFocus}
          onReady={editor => {
            const editingView = editor.editing.view;
            // Past handler
            editingView.document.on('paste', handlePaste);
          }}
        />
      </Box>
    </BaseInputLayout>
  );
};

export default WysiwygInputLayout;
